import utils from "../../../../utils/utils.js";

const actions = {
  // ============================================================================
  // SNIFFDECK PATTERN: Clean actions without try-catch
  // ============================================================================
  
  async loadQueues({ commit }) {
    commit('SET_LOADING', true);
    
    const response = await utils.utility.fetch("api/workspace/getworkspacequeues", "GET", null, null);
    const responsePayload = response?.data || response;
    const queueList = Array.isArray(responsePayload?.data)
      ? responsePayload.data
      : Array.isArray(responsePayload)
        ? responsePayload
        : [];

    const allQueues = queueList.map((queue) => {
      const rawId = queue?.id ?? queue?.Id;
      const parsedId = Number(rawId);

      return {
        ...queue,
        id: Number.isNaN(parsedId) ? rawId : parsedId,
        name: queue?.name ?? queue?.queueName ?? queue?.QueueName ?? ''
      };
    });
    
    commit('SET_QUEUES', allQueues);
    commit('SET_LOADING', false);
    return { success: true, data: allQueues };
  },

  // Use shared cross-module action - Sniffdeck pattern
  async loadAccounts({ dispatch }) {
    const response = await dispatch('dashboard/shared/loadWorkspaceAccounts', null, { root: true });
    return { success: true, data: response?.data || response || [] };
  },

  // Simplified queue updates
  async updateQueues({ commit }, queues) {
    commit('SET_LOADING', true);
    const normalized = Array.isArray(queues) ? queues : [];
    commit('SET_QUEUES', normalized);
    commit('SET_LOADING', false);
    return { success: true, data: normalized };
  },

  // Clean data fetching - Sniffdeck pattern
  async fetchMeaningAnalysisDurationData({ commit }, { widgetId, ...payload }) {    
    commit('SET_MEANING_ANALYSIS_LOADING', { widgetId, loading: true });
    commit('SET_MEANING_ANALYSIS_ERROR', { widgetId, error: null });

    const { analysisType, selectedAccounts, selectedQueues, startDate, endDate } = payload;
    const analysisData = analysisType === '1' ? selectedAccounts : selectedQueues;
    
    // Format date for C# DateTime compatibility
    const formatDate = (isoString) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    // Prepare request body for POST
    const requestBody = {
      dashboardType: 3,
      widgetType: 1,
      analysisDataType: analysisType === '1' ? 1 : 2,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      analysisData: analysisData
    };
    const response = await utils.utility.fetch(`api/Dashboard/GetOperationAnalysisAndReportingData`, "POST", null, requestBody);
    
    // Extract data from response
    const responsePayload = response?.data || response;
    const data = responsePayload?.data !== undefined ? responsePayload.data : responsePayload;
    
    commit('SET_MEANING_ANALYSIS_DATA', { widgetId, data });
    commit('SET_MEANING_ANALYSIS_LOADING', { widgetId, loading: false });
    return { success: true, data };
  },

  async fetchOperationAnalysisAndReportingData({ commit }, { widgetId, ...payload }) {    
    commit('SET_GENERIC_LOADING', { widgetId, loading: true });
    commit('SET_GENERIC_ERROR', { widgetId, error: null });

    const { analysisType, selectedAccounts, selectedQueues, startDate, endDate } = payload;
    const analysisData = analysisType === '1' ? selectedAccounts : selectedQueues;
    
    const formatDate = (isoString) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const requestBody = {
      dashboardType: 3,
      widgetType: payload.widgetType || 2,
      analysisDataType: analysisType === '1' ? 1 : 2,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      analysisData: analysisData
    };
    const response = await utils.utility.fetch(`api/Dashboard/GetOperationAnalysisAndReportingData`, "POST", null, requestBody);
    
    // Extract data from response
    const responsePayload = response?.data || response;
    const data = responsePayload?.data !== undefined ? responsePayload.data : responsePayload;
    
    commit('SET_GENERIC_DATA', { widgetId, data });
    commit('SET_GENERIC_LOADING', { widgetId, loading: false });
    return { success: true, data };
  },

  async fetchOperationAnalysisAndReportingDataByFeeder({ commit }, { widgetId, ...payload }) {
    commit('SET_GENERIC_LOADING', { widgetId, loading: true });
    commit('SET_GENERIC_ERROR', { widgetId, error: null });

    const { analysisType, selectedAccounts, selectedQueues, startDate, endDate } = payload;
    const analysisData = analysisType === '1' ? selectedAccounts : selectedQueues;

    const formatDate = (isoString) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const requestBody = {
      analysisDataType: analysisType === '1' ? 1 : 2,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      analysisData: analysisData
    };

    const response = await utils.utility.fetch(`api/Dashboard/GetOperationAnalysisAndReportingDataByFeeder`, "POST", null, requestBody);
    const responsePayload = response?.data || response;
    const data = responsePayload?.data !== undefined ? responsePayload.data : responsePayload;

    commit('SET_GENERIC_DATA', { widgetId, data });
    commit('SET_GENERIC_LOADING', { widgetId, loading: false });
    return { success: true, data };
  },

  async fetchOperationAnalysisAndReportingDataForTime({ commit }, { widgetId, ...payload }) {    
    commit('SET_TIME_LOADING', { widgetId, loading: true });
    commit('SET_TIME_ERROR', { widgetId, error: null });

    const { startDate, endDate } = payload;
    
    const formatDate = (isoString) => {
      if (!isoString) return '';
      const date = new Date(isoString);
      if (isNaN(date.getTime())) return '';
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    };

    const requestBody = {
      dashboardType: 3,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
    const response = await utils.utility.fetch(`api/Dashboard/GetOperationAnalysisAndReportingDataForTime`, "POST", null, requestBody);
    
    const responsePayload = response?.data || response;
    const data = responsePayload?.data !== undefined ? responsePayload.data : responsePayload;
    
    commit('SET_TIME_DATA', { widgetId, data });
    commit('SET_TIME_LOADING', { widgetId, loading: false });

    return { success: true, data };
  }
};

export default actions;
